import React, { useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Trash2, Link, File } from "lucide-react";
import { Lab, InsertLab } from "../../lib/supabase";
import { supabase } from "../../lib/supabase";

// Custom types for our form
type ResourceInput = {
  id?: string;
  type: "url" | "file";
  title: string;
  url?: string | null;
  file_path?: string | null;
  file_name?: string | null;
  file_type?: string | null;
};

type LearningOutcome = {
  id?: string;
  value: string;
};

interface LabFormValues {
  title: string;
  description: string | null;
  order_number: number;
  learning_outcomes: LearningOutcome[];
  resources: ResourceInput[];
  mindfulness_topic: string | null;
  coding_topic: string | null;
  objective: string | null;
  series_id?: string;
}

interface LabFormProps {
  defaultValues?: Lab;
  onSubmit: (data: InsertLab) => void;
  isLoading: boolean;
  onCancel?: () => void;
}

const LabForm: React.FC<LabFormProps> = ({
  defaultValues,
  onSubmit,
  isLoading,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LabFormValues>({
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || null,
      order_number: defaultValues?.order_number || 1,
      learning_outcomes:
        defaultValues?.learning_outcomes?.map((outcome) => ({
          value: outcome,
        })) || [],
      resources:
        defaultValues?.resources?.map((r) => ({
          type: r.type,
          title: r.title,
          url: r.url,
          file_path: r.file_path,
          file_name: r.file_name,
          file_type: r.file_type,
        })) || [],
      mindfulness_topic: defaultValues?.mindfulness_topic || null,
      coding_topic: defaultValues?.coding_topic || null,
      objective: defaultValues?.objective || null,
    },
  });

  const {
    fields: learningOutcomeFields,
    append: appendOutcome,
    remove: removeOutcome,
  } = useFieldArray({
    control,
    name: "learning_outcomes",
  });

  const {
    fields: resourceFields,
    append: appendResource,
    remove: removeResource,
  } = useFieldArray({
    control,
    name: "resources",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split(".").pop();
    const filePath = `${Math.random()}.${fileExt}`;
    const titleInput = document.getElementById("fileTitle") as HTMLInputElement;
    const title = titleInput?.value.trim() || file.name;

    try {
      const { error: uploadError } = await supabase.storage
        .from("lab-resources")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const resource: ResourceInput = {
        type: "file",
        title: title,
        file_path: filePath,
        file_name: file.name,
        file_type: file.type,
      };

      appendResource(resource);
      if (titleInput) titleInput.value = "";
    } catch (error) {
      console.error("Error uploading file:", error);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUrlAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const input = e.currentTarget;
      const url = input.value.trim();
      const titleInput = document.getElementById(
        "urlTitle"
      ) as HTMLInputElement;
      const title = titleInput?.value.trim() || url;

      if (url) {
        try {
          new URL(url);

          const resource: ResourceInput = {
            type: "url",
            title: title,
            url: url,
          };

          appendResource(resource);
          input.value = "";
          if (titleInput) titleInput.value = "";
        } catch {
          console.error("Invalid URL");
        }
      }
    }
  };

  const handleOutcomeAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = e.currentTarget.value.trim();
      if (value) {
        appendOutcome({ value });
        e.currentTarget.value = "";
      }
    }
  };

  const handleFormSubmit = (data: LabFormValues) => {
    const formattedData: InsertLab = {
      ...data,
      series_id: defaultValues?.series_id || "",
      learning_outcomes: data.learning_outcomes.map((outcome) => outcome.value),
      resources: data.resources.map((resource) => ({
        ...resource,
        lab_id: defaultValues?.id || "",
      })) as any,
    };
    onSubmit(formattedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="title" className="label">
              Title <span className="text-error-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              className={`input ${
                errors.title
                  ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                  : ""
              }`}
              {...register("title", { required: "Title is required" })}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-error-500">
                {errors.title.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="order_number" className="label">
              Order Number <span className="text-error-500">*</span>
            </label>
            <input
              id="order_number"
              type="number"
              min="1"
              className={`input ${
                errors.order_number
                  ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                  : ""
              }`}
              {...register("order_number", {
                required: "Order number is required",
                min: { value: 1, message: "Must be at least 1" },
                valueAsNumber: true,
              })}
            />
            {errors.order_number && (
              <p className="mt-1 text-sm text-error-500">
                {errors.order_number.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="mindfulness_topic" className="label">
              Mindfulness Topic
            </label>
            <input
              id="mindfulness_topic"
              type="text"
              className="input"
              {...register("mindfulness_topic")}
            />
          </div>

          <div>
            <label htmlFor="coding_topic" className="label">
              Coding Topic
            </label>
            <input
              id="coding_topic"
              type="text"
              className="input"
              {...register("coding_topic")}
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="description" className="label">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              className="input"
              placeholder="Provide a detailed description of the lab..."
              {...register("description")}
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="objective" className="label">
              Objective
            </label>
            <textarea
              id="objective"
              rows={3}
              className="input"
              {...register("objective")}
            />
          </div>

          <div className="md:col-span-2">
            <label className="label">Learning Outcomes</label>
            <div className="space-y-2">
              {learningOutcomeFields.map((field, index) => (
                <div key={field.id} className="flex gap-2">
                  <input
                    type="text"
                    className="input flex-1"
                    placeholder="Enter a learning outcome"
                    {...register(`learning_outcomes.${index}.value` as const)}
                  />
                  <button
                    type="button"
                    onClick={() => removeOutcome(index)}
                    className="icon-delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
              <input
                type="text"
                className="input w-full"
                placeholder="Add a learning outcome and press Enter"
                onKeyDown={handleOutcomeAdd}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="label">Resources</label>
            <div className="space-y-2">
              {resourceFields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2">
                  {field.type === "url" ? (
                    <>
                      <Link className="h-5 w-5 text-neutral-500" />
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          className="input flex-1"
                          placeholder="Resource name"
                          defaultValue={field.title}
                          {...register(`resources.${index}.title`)}
                        />
                        <input
                          type="text"
                          className="input flex-1"
                          placeholder="URL"
                          defaultValue={field.url || ""}
                          {...register(`resources.${index}.url`)}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <File className="h-5 w-5 text-neutral-500" />
                      <div className="flex-1">
                        <div className="input flex items-center">
                          <div className="flex-1">
                            <input
                              type="text"
                              className="border-0 bg-transparent p-0 focus:ring-0 w-full"
                              defaultValue={field.title}
                              placeholder="Resource name"
                              {...register(`resources.${index}.title`)}
                            />
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {field.file_type?.startsWith("image/") &&
                              field.file_path && (
                                <img
                                  src={`${
                                    import.meta.env.VITE_SUPABASE_URL
                                  }/storage/v1/object/public/lab-resources/${
                                    field.file_path
                                  }`}
                                  alt="Preview"
                                  className="h-6 w-6 object-cover rounded"
                                />
                              )}
                            <span className="text-xs bg-neutral-100 px-2 py-0.5 rounded">
                              {field.file_type?.split("/")[1]?.toUpperCase() ||
                                "FILE"}
                            </span>
                            <span className="text-neutral-600 text-sm">
                              {field.file_name}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => removeResource(index)}
                    className="icon-delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}

              <div>
                <div className="grid grid-cols-2 gap-2 relative">
                  <div className="border border-neutral-300 p-4 rounded rounded-md">
                    <p className="text-sm mb-1">Add URL Resource</p>
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        id="urlTitle"
                        className="input w-full"
                        placeholder="Resource name (e.g. Documentation)"
                      />
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="Enter URL and press Enter"
                        onKeyDown={handleUrlAdd}
                      />
                    </div>
                  </div>

                  {/* <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <span className="bg-white px-2 py-1 text-sm text-neutral-500">
                      or
                    </span>
                  </div> */}

                  <div className="border border-neutral-300 p-4 rounded rounded-md">
                    <p className="text-sm mb-1">Add File Resource</p>
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        id="fileTitle"
                        className="input w-full"
                        placeholder="Resource name (optional)"
                      />
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn btn-outline-green"
                        >
                          Choose File
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-outline">
            Cancel
          </button>
        )}
        <button type="submit" disabled={isLoading} className="btn btn-primary">
          {isLoading ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export default LabForm;
